/**
 * RulerStore.js
 *
 * Owns the *finished* ruler measurements. RulerManager only draws the
 * rubber-band preview between the first and second click; as soon as the second
 * point is committed the measurement is handed over to this module, which keeps
 * the line and its label in the scene until the user deletes it.
 */

import * as THREE from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import { COLORS } from "../Global/materials";
import { pushUndoAction } from "../Global/UndoManager";

// Scene units are metres; every measurement in the app is reported in feet.
const METERS_TO_FEET = 3.281;

// Rulers draw dark yellow; hover brightens it and selection switches to blue.
const STATE_COLORS = {
  idle: COLORS.ruler,
  hover: COLORS.rulerHover,
  selected: COLORS.rulerSelected,
};


export const RULER_DELETE_REQUESTED_EVENT = "RULER_DELETE_REQUESTED";

const PICK_RATIO = 0.008;
const MIN_PICK_THRESHOLD = 0.12;

// Ignore accidental double clicks that would create a zero-length measurement.
const MIN_RULER_LENGTH = 1e-3;

export const RULERS = [];

let hoveredRuler = null;
let selectedRuler = null;
let rulerIdCounter = 1;

export function getRulerMetrics(start, end) {
  const rise = Math.abs(end.z - start.z);
  const run = Math.hypot(end.x - start.x, end.y - start.y);
  const isVertical = run <= 1e-6;

  return {
    lengthFt: start.distanceTo(end) * METERS_TO_FEET,
    riseFt: rise * METERS_TO_FEET,
    runFt: run * METERS_TO_FEET,
    pitchDegrees: isVertical ? 90 : THREE.MathUtils.radToDeg(Math.atan(rise / run)),
    pitchPer12: isVertical ? null : (rise / run) * 12,
  };
}

function formatLength(metrics) {
  return `${metrics.lengthFt.toFixed(2)} ft`;
}

function formatPitch(metrics) {
  return `Pitch ${metrics.pitchDegrees.toFixed(1)}°`;
}

// Builds the CSS2D label. The wrapper inherits `pointer-events: none` from the
// label renderer so it never blocks canvas interaction; only the delete button
// opts back in.
function createRulerLabel(ruler) {
  const wrapper = document.createElement("div");
  wrapper.className = "ruler-label";

  const value = document.createElement("div");
  value.className = "ruler-label__value";

  const pitch = document.createElement("div");
  pitch.className = "ruler-pitch";

  wrapper.appendChild(value);

  return { wrapper, value, pitch };
}

function refreshRulerLabel(ruler) {
  const { elements, metrics, state } = ruler;

  elements.value.innerText = formatLength(metrics);
  elements.pitch.innerText = formatPitch(metrics);
  elements.wrapper.classList.toggle("ruler-label--hover", state === "hover");
  elements.wrapper.classList.toggle("ruler-label--selected", state === "selected");
}

function applyRulerState(ruler, state) {
  if (!ruler || ruler.state === state) return;
  ruler.state = state;
  ruler.line.material.color.set(STATE_COLORS[state]);
  ruler.caps.material.color.set(STATE_COLORS[state]);
  refreshRulerLabel(ruler);
}

/**
 * Adds a finished measurement to the scene.
 *
 * @returns {Object|null} the stored ruler, or null when the span is degenerate
 */
export function createRuler(scene, start, end, options = {}) {
  if (!scene || !start || !end) return null;

  const startPoint = new THREE.Vector3(start.x, start.y, start.z);
  const endPoint = new THREE.Vector3(end.x, end.y, end.z);
  if (startPoint.distanceTo(endPoint) < MIN_RULER_LENGTH) return null;

  const points = [startPoint, endPoint];

  const line = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(points),
    new THREE.LineBasicMaterial({ color: STATE_COLORS.idle, depthTest: false })
  );
  line.frustumCulled = false;
  line.renderOrder = 4;

  // End caps so a finished ruler reads as a measurement rather than a stray line.
  const caps = new THREE.Points(
    new THREE.BufferGeometry().setFromPoints(points),
    new THREE.PointsMaterial({
      color: STATE_COLORS.idle,
      size: 7,
      sizeAttenuation: false,
      depthTest: false,
    })
  );
  caps.frustumCulled = false;
  caps.renderOrder = 5;

  const group = new THREE.Group();
  group.name = "ruler";
  group.add(line);
  group.add(caps);

  const ruler = {
    id: rulerIdCounter++,
    start: startPoint,
    end: endPoint,
    mid: new THREE.Vector3().lerpVectors(startPoint, endPoint, 0.5),
    metrics: getRulerMetrics(startPoint, endPoint),
    state: "idle",
    scene,
    group,
    line,
    caps,
  };

  const elements = createRulerLabel(ruler);
  ruler.elements = elements;

  const label = new CSS2DObject(elements.wrapper);
  label.position.copy(ruler.mid);
  ruler.label = label;
  group.add(label);

  // Pitch readout: parked on the line, moved to the pointer while hovering.
  // center.y > 1 lifts it clear of the cursor instead of sitting under it.
  const pitchLabel = new CSS2DObject(elements.pitch);
  pitchLabel.center.set(0.5, 1.25);
  pitchLabel.position.copy(ruler.mid);
  pitchLabel.visible = false;
  ruler.pitchLabel = pitchLabel;
  group.add(pitchLabel);

  refreshRulerLabel(ruler);
  scene.add(group);
  RULERS.push(ruler);

  if (options.recordUndo) {
    pushUndoAction({
      label: "Add measurement",
      undo: () => deleteRuler(ruler),
      redo: () => restoreRuler(ruler),
    });
  }

  return ruler;
}

// Takes a ruler out of the scene. The CSS2DObject has to be removed from its own
// parent: that is what fires the `removed` event which detaches the label element
// from the DOM. Removing only the group would leave the label floating on screen.
function detachRuler(ruler) {
  ruler.group.remove(ruler.label);
  ruler.group.remove(ruler.pitchLabel);
  ruler.group.parent?.remove(ruler.group);
}

// Re-adds a ruler that was removed (used by undo/redo).
function restoreRuler(ruler) {
  if (!ruler || RULERS.includes(ruler)) return;
  ruler.group.add(ruler.label);
  ruler.group.add(ruler.pitchLabel);
  ruler.scene.add(ruler.group);
  RULERS.push(ruler);
}

export function deleteRuler(ruler, options = {}) {
  const index = RULERS.indexOf(ruler);
  if (index === -1) return;

  RULERS.splice(index, 1);
  if (hoveredRuler === ruler) hoveredRuler = null;
  if (selectedRuler === ruler) selectedRuler = null;

  detachRuler(ruler);
  ruler.pitchLabel.visible = false;
  applyRulerState(ruler, "idle");

  if (options.recordUndo) {
    pushUndoAction({
      label: "Delete measurement",
      undo: () => restoreRuler(ruler),
      redo: () => deleteRuler(ruler),
    });
  }
}

export function clearRulers() {
  hoveredRuler = null;
  selectedRuler = null;

  while (RULERS.length) {
    const ruler = RULERS.pop();
    detachRuler(ruler);
    ruler.line.geometry.dispose();
    ruler.line.material.dispose();
    ruler.caps.geometry.dispose();
    ruler.caps.material.dispose();
  }
}

/**
 * Nearest ruler under the pointer.
 *
 * @returns {{ruler: Object, point: THREE.Vector3}|null} the ruler plus the point
 *          on its line closest to the cursor (three's Line raycast reports the
 *          on-segment point, which is exactly where the pitch readout belongs).
 */
function pickRuler(raycaster, camera) {
  if (!raycaster || !RULERS.length) return null;

  const previousThreshold = raycaster.params?.Line?.threshold;
  let picked = null;
  let pickedDistance = Infinity;

  for (const ruler of RULERS) {
    const cameraDistance = camera ? camera.position.distanceTo(ruler.mid) : 1;
    raycaster.params.Line = {
      threshold: Math.max(MIN_PICK_THRESHOLD, cameraDistance * PICK_RATIO),
    };

    const hits = raycaster.intersectObject(ruler.line, false);
    if (hits.length > 0 && hits[0].distance < pickedDistance) {
      picked = { ruler, point: hits[0].point };
      pickedDistance = hits[0].distance;
    }
  }

  if (previousThreshold === undefined) {
    delete raycaster.params.Line;
  } else {
    raycaster.params.Line = { threshold: previousThreshold };
  }

  return picked;
}

export function updateRulerHover(raycaster, camera) {
  const hit = pickRuler(raycaster, camera);
  const ruler = hit?.ruler ?? null;

  if (hoveredRuler && hoveredRuler !== ruler) {
    hoveredRuler.pitchLabel.visible = false;
    if (hoveredRuler !== selectedRuler) applyRulerState(hoveredRuler, "idle");
  }

  hoveredRuler = ruler;

  if (ruler) {
    // Runs on every move, not just on enter, so the readout tracks the cursor.
    ruler.pitchLabel.position.copy(hit.point);
    ruler.pitchLabel.visible = true;
    if (ruler !== selectedRuler) applyRulerState(ruler, "hover");
  }

  return ruler;
}

export function clearRulerHover() {
  if (!hoveredRuler) return;
  hoveredRuler.pitchLabel.visible = false;
  if (hoveredRuler !== selectedRuler) applyRulerState(hoveredRuler, "idle");
  hoveredRuler = null;
}

/**
 * Selects the ruler under the pointer.
 *
 * @returns {boolean} true when a ruler was hit, so the caller can skip the
 *                    segment/panel/obstacle selection pass.
 */
export function selectRulerAt(raycaster, camera) {
  const ruler = pickRuler(raycaster, camera)?.ruler ?? null;
  if (!ruler) return false;

  if (selectedRuler && selectedRuler !== ruler) {
    applyRulerState(selectedRuler, "idle");
  }

  selectedRuler = ruler;
  applyRulerState(ruler, "selected");
  return true;
}

export function clearRulerSelection() {
  if (!selectedRuler) return;
  applyRulerState(selectedRuler, selectedRuler === hoveredRuler ? "hover" : "idle");
  selectedRuler = null;
}

export function getSelectedRuler() {
  return selectedRuler;
}

/**
 * Asks the UI to confirm deleting a ruler. The actual removal happens in
 * Designer.jsx once the shared delete modal is confirmed.
 */
export function requestRulerDelete(ruler) {
  if (!ruler || !RULERS.includes(ruler)) return false;
  if (typeof window === "undefined") return false;

  window.dispatchEvent(
    new CustomEvent(RULER_DELETE_REQUESTED_EVENT, { detail: { ruler } })
  );
  return true;
}

/** Delete/Backspace entry point. Returns true when a ruler was selected. */
export function requestSelectedRulerDelete() {
  return requestRulerDelete(selectedRuler);
}

/** Save payload: the two measured endpoints are all that is needed. */
export function serializeRulers() {
  return RULERS.map((ruler) => ({
    start: { x: ruler.start.x, y: ruler.start.y, z: ruler.start.z },
    end: { x: ruler.end.x, y: ruler.end.y, z: ruler.end.z },
  }));
}

/** Rebuilds saved rulers after a design loads. */
export function restoreRulers(scene, savedRulers) {
  clearRulers();
  if (!Array.isArray(savedRulers)) return;

  savedRulers.forEach((data) => {
    const start = data?.start;
    const end = data?.end;
    if (!start || !end) return;
    createRuler(scene, start, end);
  });
}
